import { useMeasureDirty } from '@app/core/hooks/useMeasureDirty';
import { Grid } from '@mui/material';
import { useEffect, useRef } from 'react';

interface GridItemWidthSentinelProps {
  /** MUI Grid size value; must match real grid items so measured width is the same. */
  size: number;
  /** Called when the measured width changes (e.g. layout or resize). */
  onItemWidthChange?: (width: number) => void;
}

/**
 * Invisible grid item that measures one cell width and reports it via onItemWidthChange.
 * Rendered with same size as real items so the measured width matches grid item width.
 * Used to provide a single shared width (e.g. via context) instead of per-item ResizeObservers.
 */
export function GridItemWidthSentinel({ size, onItemWidthChange }: GridItemWidthSentinelProps) {
  const measureRef = useRef<HTMLDivElement>(null);
  const bounds = useMeasureDirty(measureRef);

  useEffect(() => {
    if (bounds.width > 0 && onItemWidthChange) {
      onItemWidthChange(bounds.width);
    }
  }, [bounds.width, onItemWidthChange]);

  return (
    <Grid
      item
      xs={size}
      sx={{
        height: 0,
        minHeight: 0,
        overflow: 'hidden',
        visibility: 'hidden',
      }}
    >
      <div ref={measureRef} style={{ width: '100%' }} />
    </Grid>
  );
}
